import { LitElement, html } from 'lit';

import { navigate } from '../../lib/router/index.js';
import { api } from '../api/index.js';
import { loadState } from '../components/load-state.js';
import { idFollowsName, newestVersion } from '../format.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { newInstancePageStyles } from '../../styles/pages/new-instance-page.js';

/**
 * A new instance, on the server of `?server=<id>` or the one chosen here, for the customer of
 * `?customer=<id>` if any: from a template it runs at once, without one its first revision is the
 * next step.
 */
class NewInstancePage extends LitElement {
  static properties = {
    serverId: { state: true },
    templateId: { state: true },
    busy: { state: true },
    actionError: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    newInstancePageStyles,
  ];

  constructor() {
    super();
    const query = new URLSearchParams(window.location.search);
    this.requested = query.get('server');
    this.customerId = query.get('customer') ?? '';
    this.serverId = '';
    this.templateId = '';
    this.busy = false;
    this.actionError = null;
    // loads once: a form must not change under the user's hands
    this.loader = { value: undefined, error: null, refresh: () => this.load() };
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  async load() {
    this.loader = { ...this.loader, value: undefined, error: null };
    this.requestUpdate();
    try {
      const [servers, customers, templates] = await Promise.all([api.servers(), api.customers(), api.templates()]);
      const usable = servers.filter((server) => server.status === 'active');
      // the server asked for, or the only one there is
      const chosen = usable.find((server) => server.id === this.requested) ?? (usable.length === 1 ? usable[0] : null);
      this.serverId = chosen?.id ?? '';
      this.loader = { ...this.loader, value: { servers: usable, customers, templates } };
    } catch (error) {
      this.loader = { ...this.loader, error };
    }
    this.requestUpdate();
  }

  get server() {
    return this.loader.value?.servers.find((server) => server.id === this.serverId) ?? null;
  }

  get template() {
    return this.loader.value?.templates.find((template) => template.id === this.templateId) ?? null;
  }

  /** Where Cancel goes: the server or the customer the page was opened from, or the workspaces. */
  get back() {
    if (this.requested) {
      return `/servers/${this.requested}`;
    }
    return this.customerId ? `/customers/${this.customerId}` : '/workspaces';
  }

  async create(event) {
    event.preventDefault();
    const form = event.target;
    const template = this.template;
    this.busy = true;
    this.actionError = null;
    try {
      const instance = await api.addInstance(this.serverId, {
        name: form.elements.id.value.trim(),
        displayName: form.elements.name.value.trim(),
        // from a template, the template's
        imageVersion: template ? null : form.elements.imageVersion.value.trim(),
        customerId: form.elements.customer.value || null,
        templateId: template?.id ?? null,
        assistantId: template ? form.elements.assistantId.value.trim() || null : null,
      });
      // from a template it already runs its revision 1: its page; otherwise the first revision is the next step
      navigate(`/workspaces/${instance.ref ?? instance.name}${template ? '' : '/edit'}`);
    } catch (error) {
      this.actionError = error.message;
      this.busy = false;
    }
  }

  renderForm({ servers, customers, templates }) {
    const server = this.server;
    const template = this.template;
    // the newest version the clawlet of the server declared
    const suggested = newestVersion(server?.imageVersions) ?? '';
    return html`
      <form @submit=${(event) => this.create(event)}>
        <div class="field">
          <label for="name">Name</label>
          <span class="help">What people call the workspace. It can be changed later.</span>
          <input id="name" name="name" type="text" required maxlength="60" placeholder="e.g. Studio Rossi" @input=${(event) => idFollowsName.name(event, 32)} />
        </div>
        <div class="field">
          <label for="id">Id</label>
          <span class="help">
            Made from the name unless you type one. Lowercase letters, digits and <code>-</code>, unique on the server. It never changes: the unit, the container and the
            volume on the server are named after it.
          </span>
          <input id="id" name="id" type="text" required pattern="[a-z0-9]([a-z0-9\\-]{0,30}[a-z0-9])?" maxlength="32" placeholder="e.g. studio-rossi" @input=${(event) => idFollowsName.id(event)} />
        </div>
        <div class="field">
          <label for="server">Server</label>
          <span class="help">Where the workspace runs.</span>
          <select id="server" name="server" required @change=${(event) => (this.serverId = event.target.value)}>
            <option value="" ?selected=${!server}>Choose a server</option>
            ${servers.map(
              (candidate) => html`<option value=${candidate.id} ?selected=${candidate.id === this.serverId}>
                ${candidate.name} (${candidate.id})${candidate.connection === 'online' ? '' : ' · offline'}
              </option>`,
            )}
          </select>
        </div>
        <div class="field">
          <label for="template">Template</label>
          <span class="help">
            From a <a href="/templates">template</a>, the workspace runs it at once as its first assistant, and its customer makes it their own. Without one, nothing runs
            until you apply its first revision.
          </span>
          <select id="template" name="template" @change=${(event) => (this.templateId = event.target.value)}>
            <option value="">No template</option>
            ${templates.map((candidate) => html`<option value=${candidate.id}>${candidate.name}</option>`)}
          </select>
        </div>
        ${template
          ? html`
              <div class="field">
                <label for="assistant-id">Assistant id</label>
                <span class="help">The one the template proposes unless you type another. It never changes.</span>
                <input
                  id="assistant-id"
                  name="assistantId"
                  type="text"
                  pattern="[a-z0-9]([a-z0-9\\-]{0,30}[a-z0-9])?"
                  maxlength="32"
                  placeholder="e.g. reception"
                  .value=${template.assistantId ?? ''}
                />
              </div>
            `
          : null}
        <div class="field">
          <label for="image-version">Image version</label>
          <span class="help">${template ? 'The one of the template.' : 'The OpenClaw image it runs: the server declares the ones it has.'}</span>
          <input
            id="image-version"
            name="imageVersion"
            type="text"
            required
            maxlength="64"
            list="image-versions"
            ?disabled=${Boolean(template)}
            .value=${template ? template.imageVersion : suggested}
          />
          <datalist id="image-versions">${(server?.imageVersions ?? []).map((version) => html`<option value=${version}></option>`)}</datalist>
        </div>
        <div class="field">
          <label for="customer">Customer</label>
          <span class="help">Its members see the workspace. It can be assigned later, from the workspace page.</span>
          <select id="customer" name="customer">
            <option value="">No customer</option>
            ${customers.map((customer) => html`<option value=${customer.id} ?selected=${customer.id === this.customerId}>${customer.name}</option>`)}
          </select>
        </div>
        <div class="buttons">
          <button class="primary" ?disabled=${this.busy}>Create workspace</button>
          <a class="button" href=${this.back}>Cancel</a>
        </div>
      </form>
    `;
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/workspaces">Workspaces</a> / new</nav>`;
    const head = html`
      <header class="page-head">
        <div>
          <h1>New workspace</h1>
        </div>
      </header>
    `;
    const pending = loadState(this.loader, 'the servers');
    if (pending) {
      return html`${crumbs}${head}${pending}`;
    }
    const loaded = this.loader.value;
    return html`
      ${crumbs} ${head}
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The workspace was not created</strong>${this.actionError}</div>` : null}
      ${loaded.servers.length === 0
        ? html`<p class="empty">No server to run it on yet: add one in <a href="/servers">Servers</a> first.</p>`
        : this.renderForm(loaded)}
    `;
  }
}

customElements.define('plane-new-instance-page', NewInstancePage);
